import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { X, Gift, Check, Loader2 } from "lucide-react";

const STORAGE_KEY = "gc_exit_intent_seen";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    let armed = true;

    const onLeave = (e) => {
      // Trigger when mouse leaves the top of the viewport
      if (!armed) return;
      if (e.clientY <= 0) {
        armed = false;
        setOpen(true);
        sessionStorage.setItem(STORAGE_KEY, "1");
      }
    };
    // Fallback for mobile: trigger after 25 seconds of activity on the page
    const mobileTimer = setTimeout(() => {
      if (armed && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
        armed = false;
        setOpen(true);
        sessionStorage.setItem(STORAGE_KEY, "1");
      }
    }, 25000);

    document.addEventListener("mouseleave", onLeave);
    return () => {
      document.removeEventListener("mouseleave", onLeave);
      clearTimeout(mobileTimer);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const r = await api.post("/leads/discount", { email });
      setCode(r.data.code);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || "Couldn't claim — please try again.");
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div data-testid="exit-popup" className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-5 animate-fade-up">
      <div className="relative w-full max-w-md border border-[#8DE038] bg-[#0F1712] p-7 md:p-9">
        <button onClick={() => setOpen(false)} data-testid="exit-popup-close" aria-label="Close" className="absolute top-3 right-3 text-[#9CAEA3] hover:text-white"><X size={20}/></button>

        {!code ? (
          <>
            <div className="w-14 h-14 rounded-full bg-[#8DE038] text-[#050806] flex items-center justify-center mb-5"><Gift size={28}/></div>
            <h2 className="font-display uppercase tracking-tighter text-3xl text-[#F0F4F1]">Wait!</h2>
            <p className="text-[#C8D4CB] mt-3 text-sm leading-relaxed">Get <span className="text-[#8DE038] font-bold">10% off</span> your first booking. Drop your email and we'll send a code that's good for 30 days.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input data-testid="exit-popup-email" required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@example.com" className="gl-input"/>
              {error && <div className="text-red-400 text-xs border border-red-500/40 bg-red-900/20 p-2">{error}</div>}
              <button data-testid="exit-popup-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18}/> : "Send my 10% off code →"}
              </button>
            </form>
            <p className="text-[10px] uppercase tracking-widest text-[#6B7C71] text-center mt-4">No spam · Unsubscribe anytime</p>
          </>
        ) : (
          <div data-testid="exit-popup-success" className="text-center">
            <div className="w-14 h-14 rounded-full bg-[#8DE038] text-[#050806] flex items-center justify-center mx-auto mb-5"><Check size={28}/></div>
            <h2 className="font-display uppercase tracking-tighter text-2xl text-[#F0F4F1]">Code on its way!</h2>
            <p className="text-[#C8D4CB] mt-3 text-sm">Check your inbox. Here's a copy if it gets lost:</p>
            <div className="my-5 p-4 border-2 border-dashed border-[#8DE038] bg-[#050806] font-display text-2xl tracking-[0.3em] text-[#8DE038]">{code}</div>
            <a href="/book" className="btn-primary w-full justify-center inline-flex">Book a Pro →</a>
          </div>
        )}
      </div>
    </div>
  );
}
